/**
 * Module Service
 * 
 * Manages module activation/deactivation and recommendations
 */

import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { MODULES, getModulesForIndustry, calculateTotalPrice } from '../data/moduleCatalog';

// ============================================================================
// Types
// ============================================================================

export interface ModuleSubscription {
  moduleId: string;
  moduleName: string;
  price: number;
  activatedAt: Date;
  status: 'trial' | 'active' | 'cancelled';
  trialEndsAt?: Date;  // 30 days from activatedAt for new customers
}

export interface ModuleRecommendation {
  moduleId: string;
  moduleName: string;
  price: number;
  reason: string;
  priority: 'required' | 'recommended' | 'optional';
  features: string[];
}

// ============================================================================
// Module Recommendations
// ============================================================================

/**
 * Get recommended modules for a specific industry
 */
export function getRecommendedModulesForIndustry(industry: string): {
  required: ModuleRecommendation[];
  recommended: ModuleRecommendation[];
  optional: ModuleRecommendation[];
} {
  const { required, recommended, optional } = getModulesForIndustry(industry);
  
  return {
    required: required.map(m => ({
      moduleId: m.id,
      moduleName: m.name,
      price: m.price,
      reason: m.description,
      priority: 'required' as const,
      features: m.features
    })),
    recommended: recommended.map(m => ({
      moduleId: m.id,
      moduleName: m.name,
      price: m.price,
      reason: m.recommendationReason?.[industry] || m.description,
      priority: 'recommended' as const,
      features: m.features
    })),
    optional: optional.map(m => ({
      moduleId: m.id,
      moduleName: m.name,
      price: m.price,
      reason: m.recommendationReason?.[industry] || m.description,
      priority: 'optional' as const,
      features: m.features
    }))
  };
}

/**
 * Get all modules with their details
 */
export function getAllAvailableModules() {
  return MODULES.map(m => ({
    moduleId: m.id,
    moduleName: m.name,
    description: m.description,
    price: m.price,
    required: m.required,
    features: m.features
  }));
}

// ============================================================================
// Module Management (Firestore)
// ============================================================================

/**
 * Get organization's active modules
 */
export async function getOrganizationModules(orgId: string): Promise<ModuleSubscription[]> {
  try {
    const orgDoc = await getDoc(doc(db, 'organizations', orgId));
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    const data = orgDoc.data();
    return data.modules || [];
  } catch (error) {
    console.error('Error getting organization modules:', error);
    throw error;
  }
}

/**
 * Activate a module for an organization
 */
export async function activateModule(
  orgId: string,
  moduleId: string,
  startTrial: boolean = true
): Promise<void> {
  try {
    const module = MODULES.find(m => m.id === moduleId);
    if (!module) {
      throw new Error(`Module ${moduleId} not found`);
    }
    
    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    const activatedAt = new Date();
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + 30);  // 30-day trial
    
    const moduleSubscription: ModuleSubscription = {
      moduleId: module.id,
      moduleName: module.name,
      price: module.price,
      activatedAt,
      status: startTrial ? 'trial' : 'active',
      ...(startTrial && { trialEndsAt })
    };
    
    // Get current modules
    const currentModules = orgDoc.data().modules || [];
    
    // Check if module is already activated
    const existingIndex = currentModules.findIndex((m: ModuleSubscription) => m.moduleId === moduleId);
    
    if (existingIndex >= 0) {
      // Update existing module
      currentModules[existingIndex] = moduleSubscription;
    } else {
      // Add new module
      currentModules.push(moduleSubscription);
    }
    
    // Calculate total price
    const totalPrice = currentModules.reduce((sum: number, m: ModuleSubscription) => {
      return sum + (m.status !== 'cancelled' ? m.price : 0);
    }, 0);
    
    // Update organization
    await updateDoc(orgRef, {
      modules: currentModules,
      totalMonthlyPrice: totalPrice,
      updatedAt: Timestamp.now()
    });
    
    console.log(`✅ Module ${moduleId} activated for organization ${orgId}`);
  } catch (error) {
    console.error('Error activating module:', error);
    throw error;
  }
}

/**
 * Deactivate a module for an organization
 */
export async function deactivateModule(orgId: string, moduleId: string): Promise<void> {
  try {
    // Don't allow deactivating required modules
    const module = MODULES.find(m => m.id === moduleId);
    if (module?.required) {
      throw new Error(`Cannot deactivate required module: ${moduleId}`);
    }
    
    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    const currentModules = orgDoc.data().modules || [];
    const moduleIndex = currentModules.findIndex((m: ModuleSubscription) => m.moduleId === moduleId);
    
    if (moduleIndex >= 0) {
      // Mark as cancelled instead of removing
      currentModules[moduleIndex].status = 'cancelled';
    }
    
    // Recalculate total price
    const totalPrice = currentModules.reduce((sum: number, m: ModuleSubscription) => {
      return sum + (m.status !== 'cancelled' ? m.price : 0);
    }, 0);
    
    await updateDoc(orgRef, {
      modules: currentModules,
      totalMonthlyPrice: totalPrice,
      updatedAt: Timestamp.now()
    });
    
    console.log(`✅ Module ${moduleId} deactivated for organization ${orgId}`);
  } catch (error) {
    console.error('Error deactivating module:', error);
    throw error;
  }
}

/**
 * Check if organization has access to a specific module
 */
export async function checkModuleAccess(orgId: string, moduleId: string): Promise<boolean> {
  try {
    const orgDoc = await getDoc(doc(db, 'organizations', orgId));
    if (!orgDoc.exists()) {
      return false;
    }
    
    const data = orgDoc.data();
    
    // Legacy Pro users have access to all modules
    if (data.billingType === 'legacy_pro') {
      return true;
    }
    
    // Check if module is active
    const modules = data.modules || [];
    const module = modules.find((m: ModuleSubscription) => 
      m.moduleId === moduleId && m.status !== 'cancelled'
    );
    
    return !!module;
  } catch (error) {
    console.error('Error checking module access:', error);
    return false;
  }
}

/**
 * Activate multiple modules at once (for onboarding)
 */
export async function activateModules(
  orgId: string,
  moduleIds: string[],
  startTrial: boolean = true
): Promise<void> {
  try {
    const activatedAt = new Date();
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + 30);
    
    const moduleSubscriptions: ModuleSubscription[] = moduleIds.map(id => {
      const module = MODULES.find(m => m.id === id);
      if (!module) {
        throw new Error(`Module ${id} not found`);
      }
      
      return {
        moduleId: module.id,
        moduleName: module.name,
        price: module.price,
        activatedAt,
        status: startTrial ? 'trial' : 'active',
        ...(startTrial && { trialEndsAt })
      };
    });
    
    const totalPrice = calculateTotalPrice(moduleIds);
    
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      modules: moduleSubscriptions,
      totalMonthlyPrice: totalPrice,
      billingType: 'module_based',
      updatedAt: Timestamp.now()
    });
    
    console.log(`✅ Activated ${moduleIds.length} modules for organization ${orgId}`);
  } catch (error) {
    console.error('Error activating modules:', error);
    throw error;
  }
}

/**
 * Convert trial to paid subscription
 */
export async function convertTrialToPaid(orgId: string, moduleId: string): Promise<void> {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    const currentModules = orgDoc.data().modules || [];
    const moduleIndex = currentModules.findIndex((m: ModuleSubscription) => m.moduleId === moduleId);
    
    if (moduleIndex >= 0 && currentModules[moduleIndex].status === 'trial') {
      currentModules[moduleIndex].status = 'active';
      delete currentModules[moduleIndex].trialEndsAt;
      
      await updateDoc(orgRef, {
        modules: currentModules,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Converted trial to paid for module ${moduleId}`);
    }
  } catch (error) {
    console.error('Error converting trial to paid:', error);
    throw error;
  }
}

// ============================================================================
// Export
// ============================================================================

export const moduleService = {
  getRecommendedModulesForIndustry,
  getAllAvailableModules,
  getOrganizationModules,
  activateModule,
  deactivateModule,
  activateModules,
  checkModuleAccess,
  convertTrialToPaid,
  calculateTotalPrice
};

